"use client";

// Step 8.5 Half A0 commit 0.2 / AC406, AC408: wizard Step 3 - business
// default branch. Optional business license + resale cert uploads to
// the mag-portal-signup-docs S3 bucket via presigned PUT URLs.
// Skip-allowed link defers docs to a follow-up email. Terms checkbox
// required.

import { useState } from "react";
import { useFormContext, type SubmitErrorHandler } from "react-hook-form";
import type { SignupFormValues } from "./SignupWizard";
import {
  primaryButtonClass,
  secondaryButtonClass,
} from "./styles";

type DocKey = "business-license" | "resale-cert";

type UploadState =
  | { kind: "idle" }
  | { kind: "uploading"; pct: number }
  | { kind: "uploaded"; objectKey: string; fileName: string; sizeKb: number }
  | { kind: "error"; message: string };

function formatKb(bytes: number): string {
  return `${Math.round(bytes / 1024)} KB`;
}

async function presignUpload(input: {
  sessionId: string;
  docKey: DocKey;
  file: File;
}): Promise<string> {
  const r = await fetch("/api/signup/upload-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: input.sessionId,
      docKey: input.docKey,
      contentType: input.file.type,
      contentLength: input.file.size,
    }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `presign ${r.status}`);
  }
  const out = (await r.json()) as { uploadUrl: string; objectKey: string };
  const put = await fetch(out.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": input.file.type },
    body: input.file,
  });
  if (!put.ok) throw new Error(`upload ${put.status}`);
  return out.objectKey;
}

function UploadZone({
  docKey,
  label,
  hint,
  sessionId,
  state,
  onUploaded,
  onCleared,
}: {
  docKey: DocKey;
  label: string;
  hint: string;
  sessionId: string;
  state: UploadState;
  onUploaded: (objectKey: string, fileName: string, sizeKb: number) => void;
  onCleared: () => void;
}) {
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setLocalError(null);
    if (file.size > 10 * 1024 * 1024) {
      setLocalError("File too large - max 10MB.");
      return;
    }
    if (
      !["application/pdf", "image/jpeg", "image/png"].includes(file.type)
    ) {
      setLocalError("Only PDF, JPG, or PNG.");
      return;
    }
    try {
      const objectKey = await presignUpload({ sessionId, docKey, file });
      onUploaded(objectKey, file.name, file.size);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Upload failed.");
    }
  }

  if (state.kind === "uploaded") {
    return (
      <div
        data-testid={`signup-upload-${docKey}-done`}
        className="mb-4 flex items-center gap-4 rounded-lg border border-[#77C1BB] bg-[#f0f9f8] p-5"
      >
        <span className="rounded-md bg-[#77C1BB] px-3 py-2 text-sm font-bold text-white">
          PDF
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-900">
            {state.fileName}
          </div>
          <div className="text-xs text-zinc-500">
            {formatKb(state.sizeKb * 1024)} &middot; {label}
          </div>
        </div>
        <button
          type="button"
          data-testid={`signup-upload-${docKey}-remove`}
          onClick={onCleared}
          className="text-xs font-semibold text-red-600 hover:underline"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <label
      data-testid={`signup-upload-${docKey}`}
      className="mb-4 block cursor-pointer rounded-lg border-2 border-dashed border-[#d9ede9] bg-[#fafbfb] p-7 text-center transition-colors hover:border-[#77C1BB] hover:bg-[#f0f9f8]"
    >
      <span className="mb-2 block text-2xl text-[#77C1BB]">⬆</span>
      <div className="text-sm font-semibold text-zinc-900">{label}</div>
      <div className="mt-1 text-xs text-zinc-500">{hint}</div>
      <input
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      {localError ? (
        <p className="mt-2 text-xs text-red-600">{localError}</p>
      ) : null}
    </label>
  );
}

export function SignupStep3Business({
  submitting,
  submitError,
  sessionId,
  onBack,
  onSubmit,
  onInvalid,
}: {
  submitting: boolean;
  submitError: string | null;
  sessionId: string;
  onBack: () => void;
  onSubmit: (v: SignupFormValues) => Promise<void> | void;
  // URGENT signup fix: RHF calls this when whole-form validation fails, so a
  // blocked submit navigates + shows an error instead of doing nothing.
  onInvalid: SubmitErrorHandler<SignupFormValues>;
}) {
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useFormContext<SignupFormValues>();
  const [licenseState, setLicenseState] = useState<UploadState>({ kind: "idle" });
  const [resaleState, setResaleState] = useState<UploadState>({ kind: "idle" });
  void watch; // ensure rerenders on state change via Hook

  return (
    <form
      data-testid="signup-step-3-business"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
    >
      <h1 className="mb-2 text-center text-2xl font-semibold text-zinc-900">
        Speed up approval
      </h1>
      <p className="mb-6 text-center text-sm text-zinc-500">
        Uploading your business documents now means we can approve you almost
        immediately. Or skip and we&apos;ll request them later.
      </p>

      <div className="mb-6 rounded-md border-l-4 border-[#77C1BB] bg-[#f0f9f8] p-4 text-sm leading-relaxed text-zinc-900">
        <strong className="text-[#4a8b85]">Why this matters:</strong>{" "}
        Florida wholesale buyers need a resale certificate to skip sales tax.
        Including it now saves a follow-up email.
      </div>

      <UploadZone
        docKey="business-license"
        label="Business license / EIN (optional)"
        hint="Drag a PDF here or click to upload."
        sessionId={sessionId}
        state={licenseState}
        onUploaded={(objectKey, fileName, sizeBytes) => {
          setValue("businessLicenseObjectKey", objectKey);
          setLicenseState({
            kind: "uploaded",
            objectKey,
            fileName,
            sizeKb: sizeBytes / 1024,
          });
        }}
        onCleared={() => {
          setValue("businessLicenseObjectKey", "");
          setLicenseState({ kind: "idle" });
        }}
      />

      <UploadZone
        docKey="resale-cert"
        label="Resale certificate (optional)"
        hint="Drag a PDF here or click to upload. Florida DR-13 or equivalent."
        sessionId={sessionId}
        state={resaleState}
        onUploaded={(objectKey, fileName, sizeBytes) => {
          setValue("resaleCertObjectKey", objectKey);
          setResaleState({
            kind: "uploaded",
            objectKey,
            fileName,
            sizeKb: sizeBytes / 1024,
          });
        }}
        onCleared={() => {
          setValue("resaleCertObjectKey", "");
          setResaleState({ kind: "idle" });
        }}
      />

      <p className="my-4 text-center text-xs text-zinc-500">
        <button
          type="button"
          data-testid="signup-step-3-skip"
          className="underline"
          onClick={() => {
            setLicenseState({ kind: "idle" });
            setResaleState({ kind: "idle" });
            setValue("businessLicenseObjectKey", "");
            setValue("resaleCertObjectKey", "");
          }}
        >
          Skip — I&apos;ll send these later
        </button>
      </p>

      <label
        className="mb-6 flex gap-3 rounded-md bg-zinc-50 p-4 text-sm text-zinc-700"
        htmlFor="signup-terms"
      >
        <input
          id="signup-terms"
          type="checkbox"
          data-testid="signup-terms"
          {...register("agreedToTerms", { required: "Please agree to the terms." })}
          className="mt-1 accent-[#77C1BB]"
        />
        <span className="flex-1 leading-relaxed">
          I agree to Magnaflor&apos;s{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signup-terms-link"
            className="text-[#77C1BB] underline"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signup-privacy-link"
            className="text-[#77C1BB] underline"
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>
      {errors.agreedToTerms ? (
        <p className="mb-3 text-xs text-red-600">
          {errors.agreedToTerms.message as string}
        </p>
      ) : null}

      {submitError ? (
        <p
          data-testid="signup-submit-error"
          className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {submitError}
        </p>
      ) : null}

      <div className="flex gap-3">
        <button
          type="button"
          data-testid="signup-step-3-back"
          onClick={onBack}
          disabled={submitting}
          className={secondaryButtonClass}
        >
          ← Back
        </button>
        <button
          type="submit"
          data-testid="signup-submit"
          disabled={submitting}
          className={`${primaryButtonClass} flex-1`}
        >
          {submitting ? "Submitting..." : "Submit application →"}
        </button>
      </div>
    </form>
  );
}
